import React from 'react'
import isNaN from 'lodash/isNaN'
import isNil from 'lodash/isNil'
import toNumber from 'lodash/toNumber'
import { useHandler } from 'src/hooks'

function parseNumber(str) {
  return str.length ? toNumber(str) : null
}

function isNumber(str) {
  return str && str.trim().length && !isNaN(parseNumber(str))
}

export default function useNumberInput({ onChange, readOnly, value }) {
  // local text state relative to what the user sees in input box
  const [text, setText] = React.useState('')

  // local parsed number state
  const [number, setNumber] = React.useState(value)

  // get new blank text by using a different length to generate it
  const getNewEmptyText = () => new Array(text.length + 2).join(' ')

  // when local text changes, synchronize it with local number if parsed numbers are different
  React.useEffect(
    () => {
      if (isNumber(text)) {
        setNumber(parseNumber(text))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [text]
  )

  // when local number changes synchronize it with local text and parent value if parsed numbers are different
  React.useEffect(
    () => {
      if (onChange && !readOnly && value !== number) {
        onChange(number)
      }

      if (parseNumber(text) !== number) {
        setText(isNil(number) ? getNewEmptyText() : number.toString())
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [number]
  )

  // when value changes, synchronize it with local number if different
  React.useEffect(
    () => {
      if (value !== number) {
        setNumber(value)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value]
  )

  return {
    // improve onChange to parse value as Float
    onChange: useHandler(value => setText(value)),

    // improve onBlur to reset input on blur if not a number
    onBlur: useHandler(() => {
      if (!isNumber(text)) {
        // if not a number, force change text input
        setText(getNewEmptyText())
        // and synchronize with null value
        onChange(null)
      }
    }),

    value: text,
  }
}
